import React, { useState, useEffect, useCallback } from 'react';
import { MppContext } from './MppContextEnhanced';
import { useLazyGetApiMasterScheduleQuery } from '../../../app/athenaApiGen';

const MppContextProvider = ({ children }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [uploadInProgress, setUploadInProgress] = useState(false);
  const [masterSchedule] = useLazyGetApiMasterScheduleQuery();

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const result = await masterSchedule().unwrap();

      // Sort schedules by start date (earliest first)
      // Create a copy of the array before sorting to avoid mutating immutable RTK Query data
      let sortedResult = result;
      if (Array.isArray(result)) {
        sortedResult = [...result].sort((a, b) => {
          const dateA = a.projectData?.startDate
            ? new Date(a.projectData.startDate)
            : new Date(0);
          const dateB = b.projectData?.startDate
            ? new Date(b.projectData.startDate)
            : new Date(0);
          return dateA - dateB;
        });
      }

      setData(sortedResult);
      setLastUpdated(new Date());
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  }, [masterSchedule]);

  // Upload notification function
  const notifyUploadStart = useCallback(() => {
    setUploadInProgress(true);
  }, []);

  const notifyUploadComplete = useCallback(async () => {
    setUploadInProgress(false);
    // Auto-refresh data after upload
    await fetchData();
  }, [fetchData]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const contextValue = {
    data,
    loading,
    error,
    refreshData: fetchData,
    lastUpdated,
    uploadInProgress,
    notifyUploadStart,
    notifyUploadComplete,
  };

  return (
    <MppContext.Provider value={contextValue}>{children}</MppContext.Provider>
  );
};

export default MppContextProvider;
